import { setProcessDocumentFileInfo, setProcessDocumentSelectedFileName, setProcessDocumentUploadIsLoading } from '../../store/processDocument/documentInfo'
import { FileInfo, useUploadFileMutation, useUploadFileRequestMutation } from '../../store/media/file'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useUpdateFileMutation } from '../../store/media/media'
import { selectAccountId } from '../../store/auth/authSlice'

import { FileKind, FileStatus } from '../../models/File'
import { useEffect, useRef } from 'react'

export const useDocumentUpload = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerUploadFileRequest] = useUploadFileRequestMutation()
  const [triggerUploadFile] = useUploadFileMutation()
  const [triggerUpdateFile] = useUpdateFileMutation()

  const requestFileUpload = async (file: File) => {
    const body = { fileName: file.name, fileSize: file.size, contentType: file.type, kind: FileKind.DOCUMENT }
    const { data: fileUploadRequestResponse } = await triggerUploadFileRequest({ accountId, body }).unwrap()
    return {
      uploadUrl: fileUploadRequestResponse.fileUploadUrl,
      uploadParameters: fileUploadRequestResponse.fileUploadParameters,
      fileInfo: fileUploadRequestResponse.fileInfo,
    }
  }

  const uploadFile = async (file: File): Promise<FileInfo | null> => {
    dispatch(setProcessDocumentUploadIsLoading(true))
    dispatch(setProcessDocumentSelectedFileName(file.name))
    try {
      const { uploadUrl, uploadParameters, fileInfo } = await requestFileUpload(file)
      const { fileId } = fileInfo
      await triggerUploadFile({ uploadUrl, uploadParameters, file }).unwrap()
      await triggerUpdateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()
      dispatch(setProcessDocumentFileInfo(fileInfo))
      return fileInfo
    } catch (e) {
      console.error(e)
      return null
    } finally {
      dispatch(setProcessDocumentUploadIsLoading(false))
    }
  }

  return { uploadFile }
}

export const useSafeTimeout = () => {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSafeTimeout = () => {
    if (ref.current) {
      clearTimeout(ref.current)
    }
  }

  useEffect(() => {
    return () => {
      clearSafeTimeout()
    }
  }, [])

  const setSafeTimeout = (callback: () => void, delay?: number) => {
    ref.current = setTimeout(callback, delay)
  }

  return { setSafeTimeout, clearSafeTimeout }
}
